// ===========================================
// Meet & Greet notes hooks (#255, enquiries Phase 4)
// ===========================================
// ONE key factory. Recording notes changes a lot: the teacher's view and list ("Notes missing"
// clears), the teacher dashboard row, the admin list/detail (`notesDueAt`, `completedAt`), the
// admin notes query, the calendar chip, and the LINKED ENQUIRY (a note moves its stage), so
// `invalidateAfterNotes` is the one place that names them all.

import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import {
  adminMeetAndGreetNotesApi,
  teacherMeetAndGreetApi,
  type RecordMeetAndGreetNotesInput,
} from '../api/meetAndGreetNotes.api';
import { apiErrorCode, apiErrorMessage } from '../utils/apiError';
import { invalidateLinkedEnquiries, meetAndGreetKeys } from './useMeetAndGreet';
import { dashboardKeys } from './useDashboard';
import { calendarKeys } from './useHybridBooking';

export const mngNotesKeys = {
  all: ['mngNotes'] as const,
  teacherLists: () => [...mngNotesKeys.all, 'teacher', 'list'] as const,
  teacherList: (needsNotes?: boolean) => [...mngNotesKeys.teacherLists(), needsNotes ? 'needs-notes' : 'all'] as const,
  teacherDetail: (id: string) => [...mngNotesKeys.all, 'teacher', 'detail', id] as const,
  adminNotes: (id: string) => [...mngNotesKeys.all, 'admin', id] as const,
};

/** Every cache a recorded note changes. */
export function invalidateAfterNotes(queryClient: QueryClient): void {
  queryClient.invalidateQueries({ queryKey: mngNotesKeys.all });
  queryClient.invalidateQueries({ queryKey: meetAndGreetKeys.all });
  queryClient.invalidateQueries({ queryKey: dashboardKeys.teacher.all });
  queryClient.invalidateQueries({ queryKey: calendarKeys.all });
  // Enquiry caches (a note moves the linked enquiry's stage) and the admin dashboard.
  invalidateLinkedEnquiries(queryClient);
}

/** What the person recording is told when the server refuses. Switches on `details.code`, never the text. */
export function recordNotesErrorMessage(error: unknown): string {
  switch (apiErrorCode(error)) {
    case 'MNG_NOTES_NOT_ALLOWED':
      return 'Notes can only be recorded once the booking is confirmed.';
    case 'MNG_NOT_SCHEDULED':
      return 'This Meet & Greet has no time set yet, so notes cannot be recorded.';
    case 'MNG_NOT_YET_HELD':
      return 'This Meet & Greet has not taken place yet. Notes can be recorded once it starts.';
    default:
      return apiErrorMessage(error, 'Failed to save the notes');
  }
}

// ---- Teacher ---------------------------------------------------------------------------------

export function useTeacherMeetAndGreets(needsNotes?: boolean) {
  return useQuery({
    queryKey: mngNotesKeys.teacherList(needsNotes),
    queryFn: () => teacherMeetAndGreetApi.list(needsNotes),
  });
}

export function useTeacherMeetAndGreet(id: string | undefined) {
  return useQuery({
    queryKey: mngNotesKeys.teacherDetail(id ?? ''),
    queryFn: () => teacherMeetAndGreetApi.get(id as string),
    enabled: !!id,
  });
}

export function useRecordTeacherNotes() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: RecordMeetAndGreetNotesInput }) =>
      teacherMeetAndGreetApi.recordNotes(id, input),
    onSuccess: () => {
      invalidateAfterNotes(queryClient);
      enqueueSnackbar('Notes saved', { variant: 'success' });
    },
    onError: (error: unknown) => {
      // The page the person is looking at may be stale (e.g. the booking was cancelled meanwhile).
      queryClient.invalidateQueries({ queryKey: mngNotesKeys.all });
      enqueueSnackbar(recordNotesErrorMessage(error), { variant: 'error' });
    },
  });
}

// ---- Admin (meet_and_greet.read / .write) -----------------------------------------------------

/** `enabled` MUST carry the caller's `meet_and_greet.read`: the route refuses anyone else. */
export function useMeetAndGreetNotes(id: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: mngNotesKeys.adminNotes(id ?? ''),
    queryFn: () => adminMeetAndGreetNotesApi.list(id as string),
    enabled: !!id && enabled,
  });
}

export function useRecordAdminNotes() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: RecordMeetAndGreetNotesInput }) =>
      adminMeetAndGreetNotesApi.recordNotes(id, input),
    onSuccess: () => {
      invalidateAfterNotes(queryClient);
      enqueueSnackbar('Notes saved', { variant: 'success' });
    },
    onError: (error: unknown) => {
      queryClient.invalidateQueries({ queryKey: mngNotesKeys.all });
      queryClient.invalidateQueries({ queryKey: meetAndGreetKeys.all });
      enqueueSnackbar(recordNotesErrorMessage(error), { variant: 'error' });
    },
  });
}
